import React, { useRef } from 'react';
import c from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import type { ImmutableRootState, Langcodes } from '@uncinc/react-drupal-core/types';
import { useEscape, useProgrammaticFocus } from '@uncinc/uncinc-react-kitchen-sink';

import SearchAllContent from '../../SearchAllContent';
import ButtonWrapper from '../../ButtonWrapper';

import { createSearchUrl } from '../../../helpers/createSearchURL';
import { APP_OPTIONS, DEFAULT_LANGUAGE, SEARCH_PATH } from '../../../config/constants';

import './style.scss';

interface SoloSearchProps {
  collapsible?: boolean;
  toggleOverlay?: (onlyClose?: boolean) => void;
  menuButtonRef?: React.Ref<HTMLElement>;
  searchInputRef?: React.Ref<HTMLInputElement>;
}

const SoloSearch: React.FC<SoloSearchProps> = React.memo(({
  collapsible = false,
  searchInputRef,
  menuButtonRef,
  toggleOverlay = () => {},
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const focusToBody = useProgrammaticFocus(document.body);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const isSearchCollapsed = useRef<boolean>(true);

  const language = useSelector<ImmutableRootState<Langcodes>, Langcodes>(
    (state) => state.getIn(['uiInternal', 'language'], DEFAULT_LANGUAGE),
  );

  const SoloSearchClassNames = c('SoloSearch', {
    'is-collapsible': collapsible,
    'is-collapsed': collapsible && isSearchCollapsed.current,
  });

  const toggleSearchBar = () => {
    if (searchInputRef && typeof searchInputRef !== 'function' && searchInputRef?.current !== document.activeElement) {
      searchInputRef.current?.focus();
    }
  };

  const closeSearch = () => {
    if (menuButtonRef && typeof menuButtonRef !== 'function') {
      menuButtonRef.current?.focus();
    }
  };

  useEscape({
    onPress: collapsible ? closeSearch : () => toggleOverlay(true),
  });

  const onSearchSubmit = (query: string) => {
    const url = createSearchUrl({
      pathname: (SEARCH_PATH as Record<string, string>)[language] ?? SEARCH_PATH.en,
      index: APP_OPTIONS.parameters.searchkitIndexAllContent,
      value: query,
    });

    try {
      history.push(url);

      if (collapsible) {
        toggleSearchBar();
      } else {
        toggleOverlay(true);
      }

      focusToBody();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div ref={wrapperRef} className={SoloSearchClassNames}>
      {!collapsible && (
        <p className="SoloSearch__label body--large">
          {t('pages.search.solo_body')}
        </p>
      )}
      {collapsible && (
        <ButtonWrapper
          className="SoloSearch__collapse-button__open"
          iconName="search"
          iconPosition="left"
          inverted
          onClick={toggleSearchBar}
          aria-label={t('components.solo_search.collapse_button_open_label')}
        >
          {t('searchkit.buttons.search')}
        </ButtonWrapper>
      )}
      <SearchAllContent className="SoloSearch__search" onlyFetchWithQuery>
        <SearchAllContent.SearchBar ref={searchInputRef} onSubmit={onSearchSubmit} fieldName="solosearch-query" />
        {collapsible && (
          <ButtonWrapper
            className="SoloSearch__collapse-button__close"
            variant="icon-only"
            iconName="close"
            type="secondary"
            onClick={closeSearch}
            aria-label={t('components.solo_search.collapse_button_close_label')}
          />
        )}
      </SearchAllContent>
    </div>
  );
});

export default SoloSearch;
